import React, { useState, useEffect, useCallback } from 'react';
import { Users, BarChart3, Clock, CreditCard, Shield, Search, ChevronDown, ChevronRight, X, RefreshCw, Loader2, ArrowLeft, Calendar, Key, Trash2, Settings, Link2, Plus, Building2, User, Copy, Check, Sparkles } from 'lucide-react';
import axios from 'axios';
axios.defaults.withCredentials = true;
import API from './apiConfig';

export default function AdminDashboard({ onBack }) {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [trialDays, setTrialDays] = useState('14');
  const [actionLoading, setActionLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [pricing, setPricing] = useState({ owner_price: 269.99, member_price: 149.99, free_analyses: 1 });
  const [pricingDirty, setPricingDirty] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [expandedCompany, setExpandedCompany] = useState(null);
  const [activeTab, setActiveTab] = useState('companies');
  const [newRefCode, setNewRefCode] = useState('');
  const [copiedRef, setCopiedRef] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [s, u, p, c] = await Promise.all([
        axios.get(`${API}/api/admin/stats`),
        axios.get(`${API}/api/admin/users`),
        axios.get(`${API}/api/admin/settings`),
        axios.get(`${API}/api/admin/companies`),
      ]);
      setStats(s.data);
      setUsers(u.data.users || []);
      setPricing(p.data);
      setCompanies(c.data.companies || []);
    } catch (e) {
      console.error('Admin fetch error', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const showMsg = (m) => { setMessage(m); setTimeout(() => setMessage(''), 4000); };

  const handleSetTrial = async (userId) => {
    setActionLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/admin/users/${userId}/trial`, { days: parseInt(trialDays) || 0 });
      showMsg(data.message);
      fetchData();
    } catch (e) { showMsg(e.response?.data?.detail || 'Fehler'); }
    setActionLoading(false);
  };

  const handleResetPassword = async (userId) => {
    setActionLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/admin/users/${userId}/reset-password`, {});
      showMsg(`Neues Passwort: ${data.temp_password}`);
    } catch (e) { showMsg(e.response?.data?.detail || 'Fehler'); }
    setActionLoading(false);
  };

  const handleDeleteUser = async (userId) => {
    setActionLoading(true);
    try {
      await axios.delete(`${API}/api/admin/users/${userId}`);
      showMsg('Nutzer gelöscht');
      setSelectedUser(null);
      fetchData();
    } catch (e) { showMsg(e.response?.data?.detail || 'Fehler'); }
    setActionLoading(false);
  };

  const handleUpdateUser = async (userId, updates) => {
    setActionLoading(true);
    try {
      await axios.patch(`${API}/api/admin/users/${userId}`, updates);
      showMsg('Nutzer aktualisiert');
      await fetchData();
    } catch (e) { showMsg(e.response?.data?.detail || 'Fehler'); }
    setActionLoading(false);
  };

  const handleCreateRefCode = async () => {
    if (!newRefCode.trim()) return;
    setActionLoading(true);
    try {
      await axios.post(`${API}/api/admin/referral-codes`, { code: newRefCode.trim() });
      showMsg(`Referral-Code "${newRefCode.trim()}" erstellt`);
      setNewRefCode('');
      fetchData();
    } catch (e) { showMsg(e.response?.data?.detail || 'Fehler'); }
    setActionLoading(false);
  };

  const copyRefLink = (code) => {
    const url = `${window.location.origin}?ref=${code}#register`;
    navigator.clipboard.writeText(url);
    setCopiedRef(code);
    setTimeout(() => setCopiedRef(null), 2000);
  };

  const handleDeleteCompany = async (companyId, name, count) => {
    setActionLoading(true);
    try {
      await axios.delete(`${API}/api/admin/companies/${companyId}`);
      showMsg(`Firma "${name}" gelöscht`);
      fetchData();
    } catch (e) { showMsg(e.response?.data?.detail || 'Fehler'); }
    setActionLoading(false);
  };

  // Group users by company
  const companiesWithUsers = companies.map(c => ({
    ...c,
    users: users.filter(u => u.company_id === c.company_id),
  }));
  const unassignedUsers = users.filter(u => !u.company_id);

  const filtered = search
    ? users.filter(u =>
        u.email?.toLowerCase().includes(search.toLowerCase()) ||
        `${u.first_name} ${u.last_name}`.toLowerCase().includes(search.toLowerCase()) ||
        u.company?.toLowerCase().includes(search.toLowerCase()) ||
        u.referral_code?.toLowerCase().includes(search.toLowerCase())
      )
    : null;

  if (loading) return <div className="min-h-screen bg-zinc-950 flex items-center justify-center"><Loader2 className="w-8 h-8 text-blue-500 animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      {/* Header */}
      <div className="border-b border-zinc-800 bg-zinc-900/80 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={onBack} className="p-1.5 hover:bg-zinc-800 rounded transition-colors"><ArrowLeft className="w-4 h-4 text-zinc-400" /></button>
            <Shield className="w-5 h-5 text-amber-400" />
            <span className="font-mono text-sm font-bold text-amber-400">Admin Dashboard</span>
          </div>
          <button onClick={fetchData} className="p-2 hover:bg-zinc-800 rounded transition-colors"><RefreshCw className="w-4 h-4 text-zinc-400" /></button>
        </div>
      </div>

      {message && (
        <div className="max-w-6xl mx-auto px-6 mt-4">
          <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg text-blue-400 text-sm font-mono select-all">{message}</div>
        </div>
      )}

      <div className="max-w-6xl mx-auto px-6 py-6 space-y-6">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'Nutzer', value: stats.total_users, icon: Users, color: 'text-blue-400' },
              { label: 'Aktive Abos', value: stats.active_subscriptions, icon: CreditCard, color: 'text-green-400' },
              { label: 'Aktive Trials', value: stats.active_trials, icon: Clock, color: 'text-amber-400' },
              { label: 'Analysen', value: stats.total_analyses, icon: BarChart3, color: 'text-purple-400' },
            ].map(s => (
              <div key={s.label} className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <s.icon className={`w-4 h-4 ${s.color}`} />
                  <span className="font-mono text-[10px] text-zinc-500 uppercase">{s.label}</span>
                </div>
                <span className={`font-mono text-2xl font-bold ${s.color}`}>{s.value}</span>
              </div>
            ))}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 bg-zinc-900 p-1 rounded-lg border border-zinc-800">
          {[
            { id: 'companies', label: 'Unternehmen & Nutzer', icon: Building2 },
            { id: 'earlybird', label: `Early Birds${users.filter(u => u.early_bird_status === 'pending').length ? ` (${users.filter(u => u.early_bird_status === 'pending').length})` : ''}`, icon: Sparkles },
            { id: 'referrals', label: 'Referral-Links', icon: Link2 },
            { id: 'settings', label: 'Einstellungen', icon: Settings },
          ].map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md font-mono text-xs transition-colors ${
                activeTab === t.id ? 'bg-zinc-800 text-white' : 'text-zinc-500 hover:text-zinc-300'
              }`}>
              <t.icon className="w-3.5 h-3.5" />
              {t.label}
            </button>
          ))}
        </div>

        {/* TAB: Companies & Users */}
        {activeTab === 'companies' && (
          <div className="space-y-4">
            {/* Search */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-3 flex items-center gap-3">
              <Search className="w-4 h-4 text-zinc-500" />
              <input data-testid="admin-user-search" value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Nutzer suchen (Name, E-Mail, Firma, Referral)..."
                className="flex-1 bg-transparent text-sm font-mono text-white placeholder-zinc-600 focus:outline-none" />
              {search && <button onClick={() => setSearch('')}><X className="w-4 h-4 text-zinc-500" /></button>}
            </div>

            {/* Search results */}
            {filtered && (
              <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
                <h3 className="font-mono text-xs text-zinc-500 mb-3">Suchergebnisse ({filtered.length})</h3>
                <div className="space-y-1">
                  {filtered.map(u => <UserRow key={u.id} user={u} selected={selectedUser?.id === u.id} onClick={() => setSelectedUser(selectedUser?.id === u.id ? null : u)} />)}
                </div>
              </div>
            )}

            {/* Companies with users */}
            {!filtered && companiesWithUsers.map(c => (
              <div key={c.company_id} className="bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden" data-testid={`company-${c.company_id}`}>
                <button
                  onClick={() => setExpandedCompany(expandedCompany === c.company_id ? null : c.company_id)}
                  className="w-full px-4 py-3 flex items-center gap-3 hover:bg-zinc-800/50 transition-colors"
                >
                  <Building2 className="w-4 h-4 text-emerald-400" />
                  <span className="font-mono text-sm text-white font-medium flex-1 text-left">{c.name}</span>
                  {c.demo_mode && <span className="font-mono text-[9px] text-amber-400 bg-amber-500/10 px-1.5 py-0.5 rounded border border-amber-500/20">Demo</span>}
                  <span className="font-mono text-[10px] text-zinc-500">{c.users.length} Nutzer</span>
                  {c.invite_code && <span className="font-mono text-[10px] text-blue-400 bg-blue-500/10 px-2 py-0.5 rounded">Invite: {c.invite_code}</span>}
                  <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform ${expandedCompany === c.company_id ? 'rotate-180' : ''}`} />
                </button>
                {expandedCompany === c.company_id && (
                  <div className="border-t border-zinc-800">
                    <div className="divide-y divide-zinc-800/50">
                      {c.users.map(u => <UserRow key={u.id} user={u} selected={selectedUser?.id === u.id} onClick={() => setSelectedUser(selectedUser?.id === u.id ? null : u)} />)}
                    </div>
                    <div className="px-4 py-2 bg-zinc-950/50 flex items-center justify-between">
                      <button
                        onClick={async () => {
                          setActionLoading(true);
                          try {
                            await axios.patch(`${API}/api/admin/companies/${c.company_id}`, { demo_mode: !c.demo_mode });
                            showMsg(c.demo_mode ? `Demo deaktiviert für ${c.name}` : `Demo aktiviert für ${c.name}`);
                            await fetchData();
                          } catch (e) { showMsg(e.response?.data?.detail || 'Fehler'); }
                          setActionLoading(false);
                        }}
                        disabled={actionLoading}
                        className={`flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-mono rounded transition-colors ${
                          c.demo_mode
                            ? 'bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 border border-amber-500/30'
                            : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700 border border-zinc-700'
                        }`}
                        data-testid={`toggle-demo-${c.company_id}`}
                      >
                        <div className={`w-7 h-3.5 rounded-full relative transition-colors ${c.demo_mode ? 'bg-amber-500' : 'bg-zinc-600'}`}>
                          <div className={`absolute top-0.5 w-2.5 h-2.5 rounded-full bg-white transition-all ${c.demo_mode ? 'left-3.5' : 'left-0.5'}`} />
                        </div>
                        Demo {c.demo_mode ? 'aktiv' : 'aus'}
                      </button>
                      <button onClick={() => handleDeleteCompany(c.company_id, c.name, c.users.length)} disabled={actionLoading}
                        className="flex items-center gap-1 px-2 py-1 text-red-400 hover:bg-red-500/10 text-[10px] font-mono rounded transition-colors">
                        <Trash2 className="w-3 h-3" /> Firma löschen
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}

            {/* Unassigned users */}
            {!filtered && unassignedUsers.length > 0 && (
              <div className="bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden">
                <div className="px-4 py-3 flex items-center gap-3">
                  <User className="w-4 h-4 text-zinc-500" />
                  <span className="font-mono text-sm text-zinc-400">Ohne Firma ({unassignedUsers.length})</span>
                </div>
                <div className="border-t border-zinc-800 divide-y divide-zinc-800/50">
                  {unassignedUsers.map(u => <UserRow key={u.id} user={u} selected={selectedUser?.id === u.id} onClick={() => setSelectedUser(selectedUser?.id === u.id ? null : u)} />)}
                </div>
              </div>
            )}
          </div>
        )}


        {/* TAB: Early Birds */}
        {activeTab === 'earlybird' && (
          <div className="space-y-4">
            {/* Pending requests */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
              <h3 className="font-mono text-xs text-zinc-500 uppercase mb-4 flex items-center gap-2"><Sparkles className="w-4 h-4 text-amber-400" /> Ausstehende Anfragen</h3>
              {users.filter(u => u.early_bird_status === 'pending').length === 0 ? (
                <p className="font-mono text-xs text-zinc-600">Keine ausstehenden Early Bird Anfragen.</p>
              ) : (
                <div className="space-y-2">
                  {users.filter(u => u.early_bird_status === 'pending').map(u => (
                    <div key={u.id} className="flex items-center gap-3 p-3 bg-amber-500/5 border border-amber-500/20 rounded-lg">
                      <div className="flex-1 min-w-0">
                        <div className="font-mono text-sm text-white">{u.first_name} {u.last_name}</div>
                        <div className="font-mono text-[10px] text-zinc-500">{u.email} | {u.company || 'Keine Firma'}</div>
                        <div className="font-mono text-[10px] text-zinc-600">Registriert: {u.created_at ? new Date(u.created_at).toLocaleDateString('de-DE') : '—'}</div>
                      </div>
                      <button
                        onClick={() => handleUpdateUser(u.id, { early_bird_status: 'approved', subscription_status: 'active' })}
                        disabled={actionLoading}
                        className="px-3 py-1.5 bg-green-600 hover:bg-green-500 disabled:bg-zinc-700 text-white font-mono text-xs rounded-md transition-colors"
                        data-testid={`approve-early-bird-${u.id}`}
                      >
                        Freischalten
                      </button>
                      <button
                        onClick={() => handleUpdateUser(u.id, { early_bird_status: 'rejected' })}
                        disabled={actionLoading}
                        className="px-3 py-1.5 bg-red-600/20 hover:bg-red-600/30 border border-red-500/30 text-red-400 font-mono text-xs rounded-md transition-colors"
                      >
                        Ablehnen
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Approved Early Birds */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
              <h3 className="font-mono text-xs text-zinc-500 uppercase mb-4">Freigeschaltete Early Birds</h3>
              {users.filter(u => u.early_bird_status === 'approved').length === 0 ? (
                <p className="font-mono text-xs text-zinc-600">Noch keine freigeschalteten Early Birds.</p>
              ) : (
                <div className="space-y-1">
                  {users.filter(u => u.early_bird_status === 'approved').map(u => (
                    <div key={u.id} className="flex items-center gap-3 px-3 py-2 bg-zinc-800/30 rounded">
                      <span className="font-mono text-xs text-white">{u.first_name} {u.last_name}</span>
                      <span className="font-mono text-[10px] text-zinc-500">{u.email}</span>
                      <span className="ml-auto font-mono text-[10px] text-green-400 bg-green-500/10 px-2 py-0.5 rounded">Aktiv (max 10 Projekte)</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}


        {/* TAB: Referrals */}
        {activeTab === 'referrals' && (
          <div className="space-y-4">
            <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
              <h3 className="font-mono text-xs text-zinc-500 uppercase mb-4 flex items-center gap-2"><Link2 className="w-4 h-4 text-blue-400" /> Neuen Referral-Code erstellen</h3>
              <div className="flex gap-3">
                <input value={newRefCode} onChange={e => setNewRefCode(e.target.value)}
                  placeholder="z.B. partner2026, youtube, messe..."
                  className="flex-1 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-md font-mono text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-blue-500" />
                <button onClick={handleCreateRefCode} disabled={actionLoading || !newRefCode.trim()}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:bg-zinc-700 disabled:text-zinc-500 text-white font-mono text-xs rounded-md transition-colors flex items-center gap-2">
                  <Plus className="w-3.5 h-3.5" /> Erstellen
                </button>
              </div>
            </div>

            {/* Existing referral stats */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
              <h3 className="font-mono text-xs text-zinc-500 uppercase mb-4">Aktive Referral-Codes</h3>
              {stats?.referral_stats?.length > 0 ? (
                <div className="space-y-2">
                  {stats.referral_stats.map(r => (
                    <div key={r.code} className="flex items-center gap-3 p-3 bg-zinc-800/50 rounded-lg">
                      <Link2 className="w-4 h-4 text-blue-400" />
                      <span className="font-mono text-sm text-white flex-1">{r.code}</span>
                      <span className="font-mono text-xs text-zinc-400">{r.count} Registrierungen</span>
                      <button onClick={() => copyRefLink(r.code)}
                        className="flex items-center gap-1 px-2 py-1 bg-zinc-700 hover:bg-zinc-600 rounded text-[10px] font-mono text-zinc-300 transition-colors">
                        {copiedRef === r.code ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />}
                        {copiedRef === r.code ? 'Kopiert!' : 'Link kopieren'}
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-zinc-600 font-mono text-xs">Noch keine Referral-Codes vorhanden.</p>
              )}
            </div>

            {/* Users by referral */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
              <h3 className="font-mono text-xs text-zinc-500 uppercase mb-4">Nutzer nach Referral</h3>
              <div className="space-y-2">
                {users.filter(u => u.referral_code).map(u => (
                  <div key={u.id} className="flex items-center gap-3 px-3 py-2 bg-zinc-800/30 rounded">
                    <span className="font-mono text-xs text-white">{u.first_name} {u.last_name}</span>
                    <span className="font-mono text-[10px] text-zinc-500">{u.email}</span>
                    <span className="ml-auto font-mono text-[10px] text-blue-400 bg-blue-500/10 px-2 py-0.5 rounded">Ref: {u.referral_code}</span>
                  </div>
                ))}
                {!users.some(u => u.referral_code) && <p className="text-zinc-600 font-mono text-xs">Noch keine Nutzer über Referral.</p>}
              </div>
            </div>
          </div>
        )}

        {/* TAB: Settings */}
        {activeTab === 'settings' && (
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4" data-testid="pricing-settings">
            <div className="flex items-center gap-2 mb-4">
              <Settings className="w-4 h-4 text-amber-400" />
              <h3 className="font-mono text-xs text-zinc-500 uppercase">Preise & Einstellungen</h3>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="font-mono text-[10px] text-zinc-500 block mb-1">Firmen-Lizenz (EUR/Monat)</label>
                <input type="number" step="0.01" value={pricing.owner_price} data-testid="admin-owner-price"
                  onChange={e => { setPricing(p => ({...p, owner_price: parseFloat(e.target.value) || 0})); setPricingDirty(true); }}
                  className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-md font-mono text-sm text-white focus:outline-none focus:ring-1 focus:ring-amber-500" />
              </div>
              <div>
                <label className="font-mono text-[10px] text-zinc-500 block mb-1">Team-Mitglied (EUR/Monat)</label>
                <input type="number" step="0.01" value={pricing.member_price} data-testid="admin-member-price"
                  onChange={e => { setPricing(p => ({...p, member_price: parseFloat(e.target.value) || 0})); setPricingDirty(true); }}
                  className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-md font-mono text-sm text-white focus:outline-none focus:ring-1 focus:ring-amber-500" />
              </div>
              <div>
                <label className="font-mono text-[10px] text-zinc-500 block mb-1">Kostenlose Analysen</label>
                <input type="number" step="1" value={pricing.free_analyses} data-testid="admin-free-analyses"
                  onChange={e => { setPricing(p => ({...p, free_analyses: parseInt(e.target.value) || 0})); setPricingDirty(true); }}
                  className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-md font-mono text-sm text-white focus:outline-none focus:ring-1 focus:ring-amber-500" />
              </div>
            </div>
            {pricingDirty && (
              <button data-testid="save-pricing-btn" disabled={actionLoading}
                onClick={async () => {
                  setActionLoading(true);
                  try { await axios.patch(`${API}/api/admin/settings`, pricing); showMsg('Preise gespeichert'); setPricingDirty(false); }
                  catch (e) { showMsg(e.response?.data?.detail || 'Fehler'); }
                  setActionLoading(false);
                }}
                className="mt-3 px-4 py-2 bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-white font-mono text-xs rounded-md transition-colors">
                Preise speichern
              </button>
            )}
          </div>
        )}

        {/* Selected User Detail Panel */}
        {selectedUser && (
          <div data-testid="admin-user-detail" className="fixed inset-y-0 right-0 w-96 bg-zinc-900 border-l border-zinc-800 shadow-2xl z-50 overflow-y-auto">
            <div className="sticky top-0 bg-zinc-900 border-b border-zinc-800 p-4 flex items-center justify-between">
              <h3 className="font-mono text-sm text-white font-bold">{selectedUser.first_name} {selectedUser.last_name}</h3>
              <button onClick={() => setSelectedUser(null)} className="p-1.5 hover:bg-zinc-800 rounded"><X className="w-4 h-4 text-zinc-400" /></button>
            </div>
            <div className="p-4 space-y-5">
              {/* User Info */}
              <div className="space-y-2">
                <h4 className="font-mono text-[10px] text-zinc-500 uppercase">Kontakt</h4>
                <div className="bg-zinc-800/50 rounded-lg p-3 space-y-1.5">
                  <div className="flex justify-between font-mono text-xs"><span className="text-zinc-500">E-Mail</span><span className="text-zinc-200">{selectedUser.email}</span></div>
                  <div className="flex justify-between font-mono text-xs"><span className="text-zinc-500">Firma</span><span className="text-zinc-200">{selectedUser.company || '—'}</span></div>
                  <div className="flex justify-between font-mono text-xs"><span className="text-zinc-500">Rolle</span><span className="text-zinc-200">{selectedUser.company_role || '—'}</span></div>
                  <div className="flex justify-between font-mono text-xs"><span className="text-zinc-500">Registriert</span><span className="text-zinc-200">{selectedUser.created_at ? new Date(selectedUser.created_at).toLocaleDateString('de-DE') : '—'}</span></div>
                  {selectedUser.referral_code && (
                    <div className="flex justify-between font-mono text-xs"><span className="text-zinc-500">Referral</span><span className="text-blue-400">{selectedUser.referral_code}</span></div>
                  )}
                </div>
              </div>

              {/* Status */}
              <div className="space-y-2">
                <h4 className="font-mono text-[10px] text-zinc-500 uppercase">Status & Abo</h4>
                <div className="bg-zinc-800/50 rounded-lg p-3 space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="font-mono text-xs text-zinc-500">Abo-Status</span>
                    <select value={selectedUser.subscription_status || 'inactive'}
                      onChange={e => handleUpdateUser(selectedUser.id, { subscription_status: e.target.value })}
                      className="bg-zinc-700 border border-zinc-600 rounded px-2 py-1 font-mono text-xs text-white focus:outline-none focus:border-blue-500">
                      <option value="active">Aktiv</option>
                      <option value="inactive">Inaktiv</option>
                      <option value="cancelled">Gekündigt</option>
                    </select>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="font-mono text-xs text-zinc-500">Rolle</span>
                    <select value={selectedUser.role || 'user'}
                      onChange={e => handleUpdateUser(selectedUser.id, { role: e.target.value })}
                      className="bg-zinc-700 border border-zinc-600 rounded px-2 py-1 font-mono text-xs text-white focus:outline-none focus:border-blue-500">
                      <option value="user">User</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                  <div className="flex justify-between font-mono text-xs">
                    <span className="text-zinc-500">Analysen</span>
                    <span className="text-zinc-200">{selectedUser.analyses_count || 0}</span>
                  </div>
                </div>
              </div>

              {/* Trial */}
              <div className="space-y-2">
                <h4 className="font-mono text-[10px] text-zinc-500 uppercase flex items-center gap-1"><Calendar className="w-3 h-3" /> Testphase</h4>
                <div className="bg-zinc-800/50 rounded-lg p-3 space-y-3">
                  {selectedUser.trial_end ? (
                    <div className="flex justify-between font-mono text-xs">
                      <span className="text-zinc-500">Trial endet am</span>
                      <span className={new Date(selectedUser.trial_end) > new Date() ? 'text-green-400' : 'text-red-400'}>
                        {new Date(selectedUser.trial_end).toLocaleDateString('de-DE')}
                        {new Date(selectedUser.trial_end) > new Date() && ` (${Math.ceil((new Date(selectedUser.trial_end) - new Date()) / 86400000)} Tage)`}
                      </span>
                    </div>
                  ) : (
                    <div className="font-mono text-xs text-zinc-500">Kein Trial aktiv</div>
                  )}
                  <div className="flex gap-2">
                    <input data-testid="admin-trial-days" type="number" value={trialDays} onChange={e => setTrialDays(e.target.value)}
                      className="w-16 px-2 py-1.5 bg-zinc-700 border border-zinc-600 rounded text-xs font-mono text-white focus:border-blue-500 focus:outline-none" />
                    <span className="self-center text-[10px] text-zinc-500 font-mono">Tage</span>
                    <button data-testid="admin-set-trial" onClick={() => handleSetTrial(selectedUser.id)} disabled={actionLoading}
                      className="ml-auto px-3 py-1.5 bg-blue-600 hover:bg-blue-500 disabled:bg-zinc-700 text-white text-xs font-mono rounded transition-colors">
                      Trial setzen
                    </button>
                  </div>
                </div>
              </div>

              {/* Password */}
              <div className="space-y-2">
                <h4 className="font-mono text-[10px] text-zinc-500 uppercase flex items-center gap-1"><Key className="w-3 h-3" /> Passwort</h4>
                <button data-testid="admin-reset-pw" onClick={() => handleResetPassword(selectedUser.id)} disabled={actionLoading}
                  className="w-full px-3 py-2 bg-amber-600/20 border border-amber-600/30 hover:bg-amber-600/30 text-amber-400 text-xs font-mono rounded-lg transition-colors">
                  Passwort zurücksetzen
                </button>
              </div>

              {/* Danger zone */}
              {selectedUser.role !== 'admin' && (
                <div className="space-y-2 pt-3 border-t border-zinc-800">
                  <h4 className="font-mono text-[10px] text-red-400 uppercase">Gefahrenzone</h4>
                  <button data-testid="admin-delete-user" onClick={() => handleDeleteUser(selectedUser.id)} disabled={actionLoading}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-red-500/10 border border-red-500/20 hover:bg-red-500/20 text-red-400 text-xs font-mono rounded-lg transition-colors">
                    <Trash2 className="w-3 h-3" /> Nutzer löschen
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// User row component
function UserRow({ user: u, selected, onClick }) {
  const isActive = u.subscription_status === 'active';
  const hasTrial = u.trial_end && new Date(u.trial_end) > new Date();
  const trialDaysLeft = hasTrial ? Math.ceil((new Date(u.trial_end) - new Date()) / 86400000) : 0;
  return (
    <button data-testid={`admin-user-${u.id}`} onClick={onClick}
      className={`w-full px-4 py-2.5 flex items-center gap-3 text-left hover:bg-zinc-800/50 transition-colors ${selected ? 'bg-zinc-800/40 border-l-2 border-blue-500' : ''}`}>
      <div className="w-7 h-7 rounded-full bg-zinc-700 flex items-center justify-center text-[10px] font-mono font-bold text-zinc-300 shrink-0">
        {(u.first_name?.[0] || '')}{(u.last_name?.[0] || '')}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-mono text-xs text-white truncate">{u.first_name} {u.last_name}</span>
          {u.role === 'admin' && <span className="px-1 py-0.5 bg-amber-500/20 text-amber-400 text-[8px] font-mono rounded shrink-0">Admin</span>}
          {u.company_role === 'owner' && u.role !== 'admin' && <span className="px-1 py-0.5 bg-emerald-500/20 text-emerald-400 text-[8px] font-mono rounded shrink-0">Owner</span>}
          {u.early_bird_status === 'pending' && <span className="px-1 py-0.5 bg-amber-500/10 text-amber-400 text-[8px] font-mono rounded shrink-0 animate-pulse">Early Bird</span>}
          {u.early_bird_status === 'approved' && <span className="px-1 py-0.5 bg-amber-500/20 text-amber-400 text-[8px] font-mono rounded shrink-0">Early Bird</span>}
          {isActive && u.role !== 'admin' && !u.early_bird && <span className="px-1 py-0.5 bg-green-500/20 text-green-400 text-[8px] font-mono rounded shrink-0">Abo</span>}
          {hasTrial && <span className="px-1 py-0.5 bg-blue-500/20 text-blue-400 text-[8px] font-mono rounded shrink-0">Trial {trialDaysLeft}d</span>}
          {!isActive && !hasTrial && u.role !== 'admin' && <span className="px-1 py-0.5 bg-zinc-700 text-zinc-500 text-[8px] font-mono rounded shrink-0">Inaktiv</span>}
        </div>
        <div className="font-mono text-[10px] text-zinc-500 truncate">{u.email}{u.referral_code ? ` | Ref: ${u.referral_code}` : ''}</div>
      </div>
      <ChevronRight className="w-3.5 h-3.5 text-zinc-600 shrink-0" />
    </button>
  );
}
